import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { getComparison } from "@/lib/compare.functions";
import {
  buildCompareRows,
  canonicalPairSlug,
  compareAnswer,
  compareNarrative,
  sameSegment,
  type SideSummary,
} from "@/lib/compare-utils";
import { breadcrumbLd, faqLd, tableDatasetLd, SITE_OG_IMAGE, pageTitle } from "@/lib/seo";
import { isIndexableModel, NOINDEX_META } from "@/lib/index-policy";
import { comparableTier } from "@/lib/brand-tiers";

export const Route = createFileRoute("/compare/$pair")({
  loader: async ({ params }) => {
    const res = await getComparison({ data: { pair: params.pair } });
    if (!res.a || !res.b) throw notFound();
    const canonicalSlug = canonicalPairSlug(
      { makeSlug: res.a.makeSlug, modelSlug: res.a.modelSlug },
      { makeSlug: res.b.makeSlug, modelSlug: res.b.modelSlug },
    );
    if (params.pair !== canonicalSlug) {
      throw redirect({
        to: "/compare/$pair",
        params: { pair: canonicalSlug },
        statusCode: 301,
      });
    }
    return res;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData?.a || !loaderData?.b) {
      return {
        meta: [
          { title: "Comparison unavailable | AutoSpecData" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    const a = loaderData.a;
    const b = loaderData.b;
    // Both orientations render the same table, so the canonical always points
    // at the alphabetically ordered slug.
    const canonicalSlug = canonicalPairSlug(
      { makeSlug: a.makeSlug, modelSlug: a.modelSlug },
      { makeSlug: b.makeSlug, modelSlug: b.modelSlug },
    );
    const path = `https://autospecdata.com/compare/${canonicalSlug}`;
    // Searchers use both orientations and usually append "specs"; the title
    // carries the canonical order plus that qualifier, the body carries the
    // reverse phrasing.
    const title = `${a.fullName} vs ${b.fullName} Specs: MPG, Cost Compared`;
    const answer = compareAnswer(a, b);
    // Only same-segment pairs between mainstream, current models earn an index
    // slot. Cross-segment novelty pairs stay browsable but noindex.
    const sideIndexable = (s: SideSummary) =>
      isIndexableModel({ makeSlug: s.makeSlug, latestYear: s.yearMax, rowCount: s.trimCount });
    // Same tier test the sitemap generator uses, so the header and the sitemap
    // can never disagree: enough data on both sides and comparable efficiency.
    const hi = Math.max(a.bestMpg ?? 0, b.bestMpg ?? 0);
    const lo = Math.min(a.bestMpg ?? 0, b.bestMpg ?? 0);
    const sameTier =
      a.trimCount >= 8 && b.trimCount >= 8 && !(hi > 0 && lo > 0 && hi / lo > 1.6);
    // Brand price tier is the missing half of the sitemap's rule: without it,
    // "Hyundai Sonata vs Rolls-Royce Spectre" stayed indexable on the page even
    // though no sitemap or internal link ever pointed at it.
    const crossShopped =
      a.makeSlug !== b.makeSlug &&
      comparableTier(
        { makeSlug: a.makeSlug, modelSlug: a.modelSlug },
        { makeSlug: b.makeSlug, modelSlug: b.modelSlug },
      );
    const indexable =
      sideIndexable(a) &&
      sideIndexable(b) &&
      sameTier &&
      crossShopped &&
      sameSegment(a.vehicleClass, b.vehicleClass);
    return {
      meta: [
        { title: pageTitle(title) },
        ...(indexable ? [] : NOINDEX_META),
        { name: "description", content: answer.slice(0, 300) },
        { property: "og:title", content: title },
        { property: "og:description", content: answer.slice(0, 300) },
        { property: "og:url", content: path },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SITE_OG_IMAGE },
        { name: "twitter:image", content: SITE_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: title,
            url: path,
            description: answer,
            inLanguage: "en-US",
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "[data-quick-answer]"],
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            tableDatasetLd({
              name: `${a.fullName} vs ${b.fullName} specification comparison`,
              path: `/compare/${canonicalSlug}`,
              description: answer.slice(0, 300),
              keywords: [a.fullName, b.fullName, "MPG", "annual fuel cost", "specs comparison"],
              rowCount: a.trimCount + b.trimCount,
              yearMin: Math.min(a.yearMin ?? 0, b.yearMin ?? 0) || null,
              yearMax: Math.max(a.yearMax ?? 0, b.yearMax ?? 0) || null,
              measures: [
                { name: "Best combined fuel economy", unitText: "MPG" },
                { name: "Estimated annual fuel cost", unitText: "USD" },
              ],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            faqLd(buildFaqs(a, b)),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Compare", path: "/compare" },
              { name: `${a.fullName} vs ${b.fullName}`, path },
            ]),
          ),
        },
      ],
    };
  },
  component: ComparePage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-2xl font-bold text-foreground">Comparison not available</h1>
      <p className="mt-2 text-muted-foreground">
        We don't have data for both of those vehicles yet.
      </p>
      <Link to="/compare" className="mt-6 inline-block text-primary underline">
        Browse popular comparisons
      </Link>
    </div>
  ),
});

function buildFaqs(a: SideSummary, b: SideSummary) {
  const faqs: { q: string; a: string }[] = [
    { q: `Which is better on gas, the ${a.fullName} or the ${b.fullName}?`, a: compareAnswer(a, b) },
  ];
  if (a.lowestFuelCost && b.lowestFuelCost) {
    const cheaper = a.lowestFuelCost < b.lowestFuelCost ? a : b;
    const other = a.lowestFuelCost < b.lowestFuelCost ? b : a;
    faqs.push({
      q: `Which costs less to run per year?`,
      a: `By EPA estimates at 15,000 miles a year, the ${cheaper.fullName} runs about $${cheaper.lowestFuelCost!.toLocaleString()} a year in fuel at its most efficient configuration, versus about $${other.lowestFuelCost!.toLocaleString()} for the ${other.fullName} — a difference of roughly $${Math.abs(a.lowestFuelCost - b.lowestFuelCost).toLocaleString()} per year.`,
    });
  }
  faqs.push({
    q: `What engines do the ${a.modelName} and ${b.modelName} offer?`,
    a: `${a.fullName}: ${a.engines.length ? a.engines.join(", ") : "no engine data on record"}. ${b.fullName}: ${b.engines.length ? b.engines.join(", ") : "no engine data on record"}.`,
  });
  faqs.push({
    q: `Are they in the same vehicle class?`,
    a:
      a.vehicleClass && a.vehicleClass === b.vehicleClass
        ? `Yes — the EPA classifies both the ${a.fullName} and the ${b.fullName} as ${a.vehicleClass}, so they compete directly.`
        : `The EPA classifies the ${a.fullName} as ${a.vehicleClass ?? "unclassified"} and the ${b.fullName} as ${b.vehicleClass ?? "unclassified"}, so they aren't a like-for-like match on size.`,
  });
  return faqs;
}

function SideCard({ side, highlight }: { side: SideSummary; highlight: boolean }) {
  return (
    <div
      className={`rounded-lg border p-5 ${
        highlight ? "border-primary bg-primary/5" : "border-border bg-card"
      }`}
    >
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {side.yearMin ? `${side.yearMin}–${side.yearMax}` : "Years unavailable"}
      </p>
      <h2 className="mt-1 text-xl font-bold text-foreground">{side.fullName}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {side.vehicleClass ?? "Class unavailable"} · {side.trimCount} configurations
      </p>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-3xl font-bold text-primary">{side.bestMpg ?? "—"}</span>
        <span className="text-sm text-muted-foreground">mpg combined (best)</span>
      </div>
      <Link
        to="/$make/$model"
        params={{ make: side.makeSlug, model: side.modelSlug }}
        className="mt-4 inline-block text-sm text-primary underline"
      >
        All {side.fullName} specs
      </Link>
    </div>
  );
}
interface Dims {
  year: number;
  lengthIn: number | null;
  widthIn: number | null;
  heightIn: number | null;
  wheelbaseIn: number | null;
  curbWeightLbs: number | null;
}

const inches = (n: number | null) => (n == null ? "—" : `${Math.round(n * 10) / 10} in`);
const pounds = (n: number | null) =>
  n == null ? "—" : `${Math.round(n).toLocaleString()} lbs`;

/** Size & dimensions block: the one spec family the compare template was missing. */
function SizeSection({
  a,
  b,
  dimsA,
  dimsB,
}: {
  a: SideSummary;
  b: SideSummary;
  dimsA: Dims | null;
  dimsB: Dims | null;
}) {
  if (!dimsA || !dimsB) return null;

  const rows: { label: string; a: number | null; b: number | null; fmt: (n: number | null) => string }[] = [
    { label: "Length", a: dimsA.lengthIn, b: dimsB.lengthIn, fmt: inches },
    { label: "Width (excl. mirrors)", a: dimsA.widthIn, b: dimsB.widthIn, fmt: inches },
    { label: "Height", a: dimsA.heightIn, b: dimsB.heightIn, fmt: inches },
    { label: "Wheelbase", a: dimsA.wheelbaseIn, b: dimsB.wheelbaseIn, fmt: inches },
    { label: "Curb weight", a: dimsA.curbWeightLbs, b: dimsB.curbWeightLbs, fmt: pounds },
  ];

  const gap = (x: number | null, y: number | null) =>
    x == null || y == null ? null : Math.round((x - y) * 10) / 10;
  const lenGap = gap(dimsA.lengthIn, dimsB.lengthIn);
  const wtGap = gap(dimsA.curbWeightLbs, dimsB.curbWeightLbs);
  const wbGap = gap(dimsA.wheelbaseIn, dimsB.wheelbaseIn);
  const longer = lenGap == null ? null : lenGap > 0 ? a : b;
  const shorter = longer === a ? b : a;
  const maxLen = Math.max(dimsA.lengthIn ?? 0, dimsB.lengthIn ?? 0);

  return (
    <>
      <h2 className="mt-10 text-xl font-bold text-foreground">
        {a.fullName} vs {b.fullName} dimensions and size
      </h2>
      {lenGap != null && longer ? (
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          The {longer.fullName} is {Math.abs(lenGap)} inches longer than the{" "}
          {shorter.fullName}
          {wbGap != null && wbGap !== 0
            ? ` on a wheelbase ${Math.abs(wbGap)} inches ${
                (wbGap > 0 ? a : b) === longer ? "longer" : "shorter"
              }`
            : ""}
          {wtGap != null && wtGap !== 0
            ? `, and ${Math.abs(wtGap).toLocaleString()} lbs ${
                (wtGap > 0 ? a : b) === longer ? "heavier" : "lighter"
              }`
            : ""}
          . {maxLen > 200
            ? "At over 200 inches, the larger of the two is past the comfortable limit of a standard 192-inch single garage bay once you allow walking room."
            : "Both clear the roughly 200 inches of usable length in a standard single garage bay."}
        </p>
      ) : null}
      <div className="mt-4 overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <caption className="sr-only">
            {a.fullName} versus {b.fullName} exterior dimensions
          </caption>
          <thead className="bg-secondary/60">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-foreground">Dimension</th>
              <th className="px-4 py-3 text-left font-semibold text-foreground">
                {a.fullName} ({dimsA.year})
              </th>
              <th className="px-4 py-3 text-left font-semibold text-foreground">
                {b.fullName} ({dimsB.year})
              </th>
              <th className="px-4 py-3 text-left font-semibold text-foreground">Difference</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const d = gap(r.a, r.b);
              return (
                <tr key={r.label} className="border-t border-border">
                  <td className="px-4 py-3 text-muted-foreground">{r.label}</td>
                  <td className="px-4 py-3 text-foreground tabular-nums">{r.fmt(r.a)}</td>
                  <td className="px-4 py-3 text-foreground tabular-nums">{r.fmt(r.b)}</td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums">
                    {d == null ? "—" : d === 0 ? "identical" : r.fmt(Math.abs(d))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Width excludes mirrors; add 8–12 inches for mirrors out. Figures are for the newest
        model year on record for each vehicle.{" "}
        <Link
          to="/car-size-comparison"
          search={{ a: `${a.makeSlug}-${a.modelSlug}`, b: `${b.makeSlug}-${b.modelSlug}` }}
          className="text-primary underline"
        >
          See both drawn to scale
        </Link>
        .
      </p>
    </>
  );
}


function ComparePage() {
  const { a, b, rivals, rivalsB, dimsA, dimsB } = Route.useLoaderData();
  const { pair } = Route.useParams();
  if (!a || !b) return null;
  const rows = buildCompareRows(a, b);
  const answer = compareAnswer(a, b);
  const faqs = buildFaqs(a, b);
  const narrative = compareNarrative(a, b);
  const aWins = rows.filter((r) => r.winner === "a").length;
  const bWins = rows.filter((r) => r.winner === "b").length;

  return (
    <div>
      <nav className="mx-auto max-w-5xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/compare" className="hover:text-foreground">
          Compare
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">
          {a.modelName} vs {b.modelName}
        </span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            {a.fullName} vs {b.fullName}: Specs Compared
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Also searched as {b.fullName} vs {a.fullName} specs — both orientations show this
            same side-by-side comparison.
          </p>
          <p
            data-quick-answer
            className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-foreground"
          >
            {answer}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Across {rows.filter((r) => r.winner).length} measured categories, the{" "}
            {aWins >= bWins ? a.fullName : b.fullName} leads in{" "}
            {Math.max(aWins, bWins)}. Figures are EPA test data, not manufacturer claims.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <SideCard side={a} highlight={aWins > bWins} />
          <SideCard side={b} highlight={bWins > aWins} />
        </div>

        <h2 className="mt-8 text-xl font-bold text-foreground">
          Head-to-head specifications
        </h2>
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-foreground">Metric</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">
                  {a.fullName}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">
                  {b.fullName}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-t border-border">
                  <td className="px-4 py-3 text-muted-foreground">
                    {r.label}
                    {r.note ? (
                      <span className="block text-xs opacity-70">{r.note}</span>
                    ) : null}
                  </td>
                  <td
                    className={`px-4 py-3 ${
                      r.winner === "a" ? "font-semibold text-primary" : "text-foreground"
                    }`}
                  >
                    {r.a}
                    {r.winner === "a" ? " ✓" : ""}
                  </td>
                  <td
                    className={`px-4 py-3 ${
                      r.winner === "b" ? "font-semibold text-primary" : "text-foreground"
                    }`}
                  >
                    {r.b}
                    {r.winner === "b" ? " ✓" : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Source: EPA fuel economy records via fueleconomy.gov. Best-case values across all
          recorded configurations for each model.
        </p>

        <SizeSection a={a} b={b} dimsA={dimsA} dimsB={dimsB} />



        {narrative.length ? (
          <>
            <h2 className="mt-10 text-xl font-bold text-foreground">
              {a.fullName} vs {b.fullName}: what the numbers mean
            </h2>
            <div className="mt-4 space-y-4 max-w-3xl">
              {narrative.map((para) => (
                <p key={para.slice(0, 40)} className="text-sm leading-relaxed text-muted-foreground">
                  {para}
                </p>
              ))}
            </div>
          </>
        ) : null}

        <h2 className="mt-8 text-xl font-bold text-foreground">
          {a.modelName} vs {b.modelName}: common questions
        </h2>
        <div className="mt-4 space-y-5">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-foreground">{f.q}</h3>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>

        {rivals.length ? (
          <>
            <h2 className="mt-10 text-xl font-bold text-foreground">
              More {a.modelName} comparisons
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {(rivals as { slug: string; label: string }[]).map((r) => (
                <Link
                  key={r.slug}
                  to="/compare/$pair"
                  params={{ pair: r.slug }}
                  className="rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  {a.modelName} vs {r.label}
                </Link>
              ))}
            </div>
          </>
        ) : null}

        {rivalsB.length ? (
          <>
            <h2 className="mt-10 text-xl font-bold text-foreground">
              More {b.modelName} comparisons
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {(rivalsB as { slug: string; label: string }[]).map((r) => (
                <Link
                  key={r.slug}
                  to="/compare/$pair"
                  params={{ pair: r.slug }}
                  className="rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  {b.modelName} vs {r.label}
                </Link>
              ))}
            </div>
          </>
        ) : null}

        <div className="mt-10 flex flex-wrap gap-3 text-sm">
          <Link
            to="/$make/$model/$dataType"
            params={{ make: a.makeSlug, model: a.modelSlug, dataType: "mpg" }}
            className="text-primary underline"
          >
            {a.fullName} MPG by year
          </Link>
          <Link
            to="/$make/$model/$dataType"
            params={{ make: b.makeSlug, model: b.modelSlug, dataType: "mpg" }}
            className="text-primary underline"
          >
            {b.fullName} MPG by year
          </Link>
          <Link to="/$make" params={{ make: a.makeSlug }} className="text-primary underline">
            All {a.makeName} models
          </Link>
          <Link to="/$make" params={{ make: b.makeSlug }} className="text-primary underline">
            All {b.makeName} models
          </Link>

          <Link to="/tools/$slug" params={{ slug: "fuel-cost-calculator" }} className="cta-gold">
            Calculate your own fuel savings
          </Link>
        </div>
        <p className="sr-only">Comparison id: {pair}</p>
      </section>
    </div>
  );
}
